/**
 * Machine-readable API error codes.
 *
 * Mirrors `wolsal_core/utils/errors.py::ErrorCode` on the backend. The backend
 * attaches a stable `code` to every error envelope so the frontend can branch
 * on it instead of parsing (localized) message strings.
 *
 * Keep these values in sync with the backend.
 */
export enum ErrorCode {
  // Generic / HTTP-status-aligned
  BAD_REQUEST = 'BAD_REQUEST',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_JSON = 'INVALID_JSON',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  RATE_LIMITED = 'RATE_LIMITED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // Domain-specific (behaviour-driving)
  EMAIL_DUPLICATE = 'EMAIL_DUPLICATE',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  ORDER_ALREADY_SENT_TO_ERP = 'ORDER_ALREADY_SENT_TO_ERP',
  ORDER_SYNC_IN_PROGRESS = 'ORDER_SYNC_IN_PROGRESS',
  /** The password was right, but a second factor is owed. Not a failure --
   *  the caller should show the challenge. */
  MFA_REQUIRED = 'MFA_REQUIRED',
  /** Wrong or expired second-factor code. The challenge stays open. */
  MFA_INVALID_CODE = 'MFA_INVALID_CODE',
  /** Policy requires a second factor this account does not have and cannot
   *  enrol without a session. Only an administrator can clear it, so the UI
   *  must say so rather than offer a retry. */
  MFA_ENROLLMENT_REQUIRED = 'MFA_ENROLLMENT_REQUIRED',

  // Frontend-only: the request never reached the server.
  NETWORK_ERROR = 'NETWORK_ERROR',
}

/**
 * Normalized representation of an HTTP error, produced by ErrorMessageService.
 * Every consumer (interceptor, effects, components) reads this shape instead of
 * re-deriving messages from the raw HttpErrorResponse.
 */
export interface AppError {
  /** Human-readable message(s) ready for display. An array renders as a list. */
  messages: string | string[];
  /** Machine-readable code (from the envelope, or inferred client-side). */
  code: ErrorCode | string | null;
  /** HTTP status code (0 when the request never reached the server). */
  status: number;
  /** Per-field validation errors when available (field name -> message). */
  fieldErrors?: Array<{ name?: string; error: string }>;
}
